import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import MobileQRButton from '../components/MobileQRButton';
import {
  Heart, Users, Stethoscope, Building2, FlaskConical, Shield,
  Brain, Activity, FileText, Calendar, Pill, Video, Star,
  ArrowRight, ChevronRight, Play, Pause, RotateCcw,
  Sparkles, Eye, BarChart3, Microscope, ClipboardCheck,
  MapPin, Search, Zap, Monitor, Leaf, CheckCircle,
  TrendingUp, Clock, Upload, Share2, Lock, UserCheck,
  Phone, AlertCircle
} from 'lucide-react';

const ROLES = [
  { id: 'user', label: 'User / Patient', icon: Users, color: 'from-blue-500 to-cyan-500', accent: '#3b82f6' },
  { id: 'doctor', label: 'Doctor', icon: Stethoscope, color: 'from-orange-500 to-amber-500', accent: '#f97316' },
  { id: 'admin', label: 'Admin', icon: Shield, color: 'from-purple-500 to-violet-500', accent: '#8b5cf6' },
  { id: 'clinic', label: 'Clinic / Hospital', icon: Building2, color: 'from-emerald-500 to-teal-500', accent: '#10b981' },
  { id: 'lab', label: 'Lab', icon: FlaskConical, color: 'from-rose-500 to-pink-500', accent: '#f43f5e' },
];

const ROLE_TOURS = {
  user: {
    title: 'Your Complete Health Companion',
    subtitle: 'Track, analyze, and take control of your health journey with AI-powered insights.',
    steps: [
      {
        title: 'Smart Registration with Location',
        description: 'Register with your location — Country, State, City & Area auto-suggest from our database of 500+ localities. Get matched with doctors near you instantly.',
        icon: MapPin,
        visual: 'registration',
        aiFeature: false,
      },
      {
        title: 'AI Lab Report Analysis',
        description: 'Upload any lab report — our AI OCR extracts every value, identifies abnormalities, and generates a comprehensive Allopathic + Ayurvedic dual analysis in seconds.',
        icon: Brain,
        visual: 'lab-analysis',
        aiFeature: true,
        aiDetail: 'Dual Medical Engine: Combines Western medicine biomarkers with traditional Ayurvedic Prakriti assessment for holistic health insights.',
      },
      {
        title: 'Find Doctors by Specialization & Location',
        description: 'Search specialists filtered by your exact area, city, or state. See ratings, reviews, experience, and consultation fees — then book instantly.',
        icon: Search,
        visual: 'find-doctor',
        aiFeature: false,
      },
      {
        title: 'AI Health Risk Assessment',
        description: 'Get personalized risk scores across 12+ health categories. Our AI analyzes your lab trends over time to predict and prevent health issues before they arise.',
        icon: Activity,
        visual: 'risk-assessment',
        aiFeature: true,
        aiDetail: 'Predictive Analytics: LSTM neural networks analyze your historical lab data to forecast health trajectories and recommend preventive measures.',
      },
      {
        title: 'Prakriti Assessment (Ayurvedic Profile)',
        description: 'Discover your unique mind-body constitution through our AI-guided Vata-Pitta-Kapha assessment. Receive personalized diet, exercise, and lifestyle recommendations.',
        icon: Leaf,
        visual: 'prakriti',
        aiFeature: true,
        aiDetail: 'Ayurvedic AI Engine: Maps your responses to classical Prakriti archetypes and generates customized wellness plans aligned with your constitution.',
      },
      {
        title: 'Wearable Data Sync & Monitoring',
        description: 'Connect Google Fit, Fitbit, and Apple Health to sync real-time vitals. Track heart rate, sleep, steps, and SpO2 — all feeding into your AI health score.',
        icon: Monitor,
        visual: 'wearables',
        aiFeature: false,
      },
      {
        title: 'AI Lifestyle Planner',
        description: 'Based on your lab results, Prakriti type, and wearable data, receive an AI-generated personalized meal plan, exercise routine, and sleep optimization strategy.',
        icon: Sparkles,
        visual: 'lifestyle',
        aiFeature: true,
        aiDetail: 'Holistic AI Planning: Integrates all your health data sources to generate daily, weekly, and monthly wellness roadmaps tailored to your goals.',
      },
    ],
  },
  doctor: {
    title: 'Intelligent Clinical Decision Support',
    subtitle: 'Enhance your practice with AI-powered diagnostics, patient management, and evidence-based insights.',
    steps: [
      {
        title: 'Role-Based Dashboard',
        description: 'A dedicated command center showing your patients, today\'s appointments, pending prescriptions, and AI-flagged cases requiring immediate attention.',
        icon: BarChart3,
        visual: 'doctor-dashboard',
        aiFeature: false,
      },
      {
        title: 'AI Clinical Insights',
        description: 'For each patient, receive AI-generated clinical summaries, differential diagnoses, and treatment recommendations based on their complete medical history.',
        icon: Brain,
        visual: 'clinical-insights',
        aiFeature: true,
        aiDetail: 'Clinical AI Engine: Processes patient labs, vitals, medications, and history to generate evidence-based diagnostic suggestions with confidence scores.',
      },
      {
        title: 'Smart Prescription Writer',
        description: 'Digital prescriptions with automatic drug interaction warnings, dosage calculators, and pharmacy integration. Supports both Allopathic and Ayurvedic medications.',
        icon: Pill,
        visual: 'prescription',
        aiFeature: true,
        aiDetail: 'Drug Safety AI: Cross-references 50,000+ drug interactions and contraindications in real-time as you prescribe.',
      },
      {
        title: 'Patient Lab Analysis Review',
        description: 'Review AI-analyzed lab reports with highlighted abnormalities, trend graphs, and comparative analysis against previous results — saving hours of manual review.',
        icon: FileText,
        visual: 'lab-review',
        aiFeature: true,
        aiDetail: 'Trend Analysis: AI overlays current results against historical data, flagging clinically significant changes and patterns.',
      },
      {
        title: 'Telemedicine & Video Consultation',
        description: 'Conduct HD video consultations with screen sharing, recording, and integrated medical notes. Includes AI-transcription of consultation notes.',
        icon: Video,
        visual: 'telemedicine',
        aiFeature: false,
      },
      {
        title: 'Patient Location Matching',
        description: 'Get discovered by patients in your area. Your profile appears in location-based searches with your specialization, ratings, and availability.',
        icon: MapPin,
        visual: 'location-matching',
        aiFeature: false,
      },
    ],
  },
  admin: {
    title: 'Complete Platform Governance',
    subtitle: 'Monitor, manage, and scale the entire healthcare ecosystem from a single dashboard.',
    steps: [
      {
        title: 'Platform Analytics Dashboard',
        description: 'Real-time statistics on users, doctors, appointments, revenue, and system health. Monitor growth trends, user engagement, and platform KPIs at a glance.',
        icon: BarChart3,
        visual: 'admin-analytics',
        aiFeature: false,
      },
      {
        title: 'User & Doctor Management',
        description: 'Manage all platform users across roles. Verify doctor credentials, approve clinic registrations, and handle user escalations from one centralized panel.',
        icon: UserCheck,
        visual: 'user-management',
        aiFeature: false,
      },
      {
        title: 'AI Usage & Analytics Monitoring',
        description: 'Track AI analysis volumes, accuracy metrics, and cost optimization. Monitor which AI features are most used and ensure quality standards are maintained.',
        icon: Brain,
        visual: 'ai-monitoring',
        aiFeature: true,
        aiDetail: 'AI Operations Dashboard: Monitors model performance, inference latency, accuracy rates, and auto-scaling of AI resources based on demand patterns.',
      },
      {
        title: 'Security & Compliance',
        description: 'HIPAA compliance monitoring, audit trails, MFA enforcement, and brute-force protection. Manage API keys, rate limits, and data access policies.',
        icon: Lock,
        visual: 'security',
        aiFeature: false,
      },
      {
        title: 'Revenue & Subscription Management',
        description: 'Track B2B and B2C revenue streams, manage subscription tiers, process refunds, and forecast revenue with AI-powered financial projections.',
        icon: TrendingUp,
        visual: 'revenue',
        aiFeature: true,
        aiDetail: 'Revenue Intelligence: AI models predict churn, identify upsell opportunities, and optimize pricing based on usage patterns and market data.',
      },
    ],
  },
  clinic: {
    title: 'Hospital & Clinic Management Suite',
    subtitle: 'Run your entire healthcare facility — departments, staff, patients, and operations — from one intelligent platform.',
    steps: [
      {
        title: 'Hospital vs Consultation Clinic Mode',
        description: 'Choose your facility type at registration. Hospitals get department management, bed tracking, and OR scheduling. Consultation clinics get streamlined patient flow.',
        icon: Building2,
        visual: 'clinic-mode',
        aiFeature: false,
      },
      {
        title: 'Department & Staff Management',
        description: 'Manage multiple departments (Cardiology, Pediatrics, Orthopedics), assign doctors, track workloads, and optimize scheduling across your entire facility.',
        icon: Users,
        visual: 'departments',
        aiFeature: false,
      },
      {
        title: 'AI Patient Flow Optimization',
        description: 'AI analyzes appointment patterns, wait times, and doctor availability to optimize patient scheduling and reduce bottlenecks across departments.',
        icon: Brain,
        visual: 'patient-flow',
        aiFeature: true,
        aiDetail: 'Flow Optimization AI: Predicts peak hours, suggests optimal appointment slots, and reduces average patient wait time by up to 40%.',
      },
      {
        title: 'Integrated Lab & Pharmacy',
        description: 'Connect your in-house lab and pharmacy for seamless test ordering, result delivery, and prescription fulfillment — all tracked within the platform.',
        icon: FlaskConical,
        visual: 'integrated-lab',
        aiFeature: false,
      },
      {
        title: 'AI Operational Analytics',
        description: 'Dashboard showing bed occupancy, revenue per department, doctor utilization, and predictive models for resource planning and capacity management.',
        icon: BarChart3,
        visual: 'operational-analytics',
        aiFeature: true,
        aiDetail: 'Operational Intelligence: Forecasts patient admissions, predicts equipment maintenance needs, and optimizes staff scheduling for cost efficiency.',
      },
    ],
  },
  lab: {
    title: 'Smart Diagnostics Lab Management',
    subtitle: 'From sample collection to AI-powered result interpretation — transform your diagnostic workflow.',
    steps: [
      {
        title: 'Lab Dashboard & Test Queue',
        description: 'View all pending, in-progress, and completed tests across categories (Hematology, Biochemistry, Microbiology, Pathology). Track turnaround times and sample status.',
        icon: ClipboardCheck,
        visual: 'lab-dashboard',
        aiFeature: false,
      },
      {
        title: 'Sample Tracking & Processing',
        description: 'Barcode-based sample tracking from collection to result. Monitor processing stages, flag urgent samples, and maintain chain-of-custody records.',
        icon: Microscope,
        visual: 'sample-tracking',
        aiFeature: false,
      },
      {
        title: 'AI-Powered Result Interpretation',
        description: 'Our AI automatically interprets lab results, flags critical values, identifies patterns across tests, and generates preliminary clinical notes for reviewing physicians.',
        icon: Brain,
        visual: 'ai-interpretation',
        aiFeature: true,
        aiDetail: 'Diagnostic AI: Analyzes results against reference ranges, patient history, and clinical guidelines to generate interpretive notes and flag urgent findings.',
      },
      {
        title: 'Quality Control & Accreditation',
        description: 'Automated QC checks, proficiency testing records, and compliance tracking for NABL/CAP accreditation. Maintain audit-ready documentation at all times.',
        icon: CheckCircle,
        visual: 'quality-control',
        aiFeature: false,
      },
      {
        title: 'AI Trend Reports & Alerts',
        description: 'Automatically detect recurring anomalies in test populations, instrument drift, or unusual result distributions. Get proactive alerts before issues impact patient care.',
        icon: Sparkles,
        visual: 'trend-alerts',
        aiFeature: true,
        aiDetail: 'Anomaly Detection: Statistical models continuously monitor result distributions, flagging outliers that may indicate instrument calibration issues or emerging health trends.',
      },
    ],
  },
};

// Animated mock screen component
const MockScreen = ({ visual, accent, isActive }) => {
  const screens = {
    // ===== USER / PATIENT SCREENS =====
    'registration': (
      <div className="space-y-3">
        <div className="h-8 bg-gray-100 rounded-lg w-3/4 animate-pulse" />
        <div className="grid grid-cols-2 gap-2">
          {['India', 'Maharashtra', 'Mumbai', 'Bandra West'].map(v => (
            <div key={v} className="h-10 bg-blue-50 rounded-lg border-2 border-blue-200 flex items-center px-3">
              <span className="text-xs text-blue-600 font-medium">{v}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-2">
          {['User', 'Doctor', 'Clinic', 'Lab'].map(r => (
            <div key={r} className={`px-3 py-1.5 rounded-full text-xs font-medium ${r === 'User' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-500'}`}>{r}</div>
          ))}
        </div>
      </div>
    ),
    'lab-analysis': (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center"><Upload className="w-4 h-4 text-white" /></div>
          <span className="text-xs font-bold text-gray-700">Lab Report Uploaded</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 bg-blue-50 rounded-lg border border-blue-200">
            <div className="text-[10px] text-blue-500 font-semibold">ALLOPATHIC</div>
            <div className="text-xs font-bold text-blue-700 mt-1">HbA1c: 6.8%</div>
            <div className="text-[10px] text-red-500">Above Normal</div>
          </div>
          <div className="p-2 bg-green-50 rounded-lg border border-green-200">
            <div className="text-[10px] text-green-500 font-semibold">AYURVEDIC</div>
            <div className="text-xs font-bold text-green-700 mt-1">Kapha Imbalance</div>
            <div className="text-[10px] text-green-600">Meda Dhatu</div>
          </div>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-full" style={{ width: '68%' }} /></div>
        <div className="text-[10px] text-gray-500 text-center">AI Confidence: 97.3%</div>
      </div>
    ),
    'find-doctor': (
      <div className="space-y-2">
        <div className="flex gap-2">
          <div className="h-8 flex-1 bg-gray-100 rounded-lg flex items-center px-2"><Search className="w-3 h-3 text-gray-400 mr-1" /><span className="text-[10px] text-gray-400">Search...</span></div>
          <div className="h-8 bg-orange-100 rounded-lg px-2 flex items-center"><span className="text-[10px] text-orange-600">Cardiology</span></div>
        </div>
        <div className="flex gap-1 flex-wrap">
          {['Bandra West', 'Mumbai', 'Maharashtra'].map(l => (<span key={l} className="px-2 py-0.5 bg-orange-500 text-white rounded-full text-[9px]">{l}</span>))}
        </div>
        {[{ name: 'Dr. Ritika Nair', spec: 'Cardiology', rating: '4.9', loc: 'Bandra West' },{ name: 'Dr. Mehta', spec: 'Cardiology', rating: '4.7', loc: 'Andheri' }].map(d => (
          <div key={d.name} className="flex items-center gap-2 p-2 bg-white rounded-lg border">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white text-[10px] font-bold">{d.name.charAt(4)}</div>
            <div className="flex-1"><div className="text-[10px] font-semibold">{d.name}</div><div className="text-[9px] text-gray-500">{d.spec} - {d.loc}</div></div>
            <div className="flex items-center gap-0.5"><Star className="w-3 h-3 text-amber-400 fill-current" /><span className="text-[10px] font-bold">{d.rating}</span></div>
          </div>
        ))}
      </div>
    ),
    'risk-assessment': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700 mb-1">AI Health Risk Score</div>
        <div className="relative w-full h-3 bg-gray-100 rounded-full overflow-hidden"><div className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-400 via-yellow-400 to-red-500 rounded-full transition-all duration-1000" style={{ width: isActive ? '72%' : '0%' }} /></div>
        <div className="grid grid-cols-3 gap-1">
          {[{ label: 'Cardiac', val: 'Low', color: 'text-green-600 bg-green-50' },{ label: 'Diabetes', val: 'Medium', color: 'text-yellow-600 bg-yellow-50' },{ label: 'Liver', val: 'Low', color: 'text-green-600 bg-green-50' },{ label: 'Kidney', val: 'Low', color: 'text-green-600 bg-green-50' },{ label: 'Thyroid', val: 'High', color: 'text-red-600 bg-red-50' },{ label: 'Lipid', val: 'Medium', color: 'text-yellow-600 bg-yellow-50' }].map(r => (
            <div key={r.label} className={`p-1.5 rounded-lg text-center ${r.color}`}><div className="text-[9px] font-medium">{r.label}</div><div className="text-[10px] font-bold">{r.val}</div></div>
          ))}
        </div>
      </div>
    ),
    'prakriti': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700">Your Prakriti Profile</div>
        <div className="flex items-end justify-center gap-4 h-20">
          {[{ type: 'Vata', pct: 25, color: 'bg-sky-400' },{ type: 'Pitta', pct: 55, color: 'bg-orange-400' },{ type: 'Kapha', pct: 20, color: 'bg-green-400' }].map(p => (
            <div key={p.type} className="flex flex-col items-center gap-1">
              <div className={`w-8 rounded-t-lg ${p.color} transition-all duration-1000`} style={{ height: isActive ? `${p.pct}px` : '4px' }} />
              <span className="text-[9px] font-medium text-gray-600">{p.type}</span>
              <span className="text-[10px] font-bold">{p.pct}%</span>
            </div>
          ))}
        </div>
        <div className="p-2 bg-orange-50 rounded-lg border border-orange-200"><div className="text-[10px] font-semibold text-orange-700">Pitta Dominant</div><div className="text-[9px] text-orange-600">Recommended: Cooling foods, moderate exercise</div></div>
      </div>
    ),
    'wearables': (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          {[{ label: 'Heart Rate', val: '72 bpm', icon: Heart, color: 'text-red-500 bg-red-50' },{ label: 'SpO2', val: '98%', icon: Activity, color: 'text-blue-500 bg-blue-50' },{ label: 'Steps', val: '8,432', icon: TrendingUp, color: 'text-green-500 bg-green-50' },{ label: 'Sleep', val: '7.2 hrs', icon: Clock, color: 'text-purple-500 bg-purple-50' }].map(w => (
            <div key={w.label} className={`p-2 rounded-lg ${w.color}`}><w.icon className="w-3 h-3 mb-1" /><div className="text-[10px] font-bold">{w.val}</div><div className="text-[9px] opacity-70">{w.label}</div></div>
          ))}
        </div>
      </div>
    ),
    'lifestyle': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700 flex items-center gap-1"><Sparkles className="w-3 h-3 text-orange-500" /> AI Lifestyle Plan</div>
        {[{ time: '6:00 AM', item: 'Yoga + Pranayama (Pitta-balancing)' },{ time: '7:30 AM', item: 'Oatmeal + berries (anti-inflammatory)' },{ time: '12:30 PM', item: 'Grilled fish + quinoa salad' },{ time: '6:00 PM', item: '30min brisk walk (8,000 steps target)' }].map(l => (
          <div key={l.time} className="flex items-center gap-2 text-[10px]"><span className="text-gray-400 w-12 font-mono">{l.time}</span><span className="text-gray-700">{l.item}</span></div>
        ))}
      </div>
    ),

    // ===== DOCTOR SCREENS =====
    'doctor-dashboard': (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-xs font-bold text-gray-700">Doctor Command Center</div>
          <span className="text-[10px] text-green-600 bg-green-50 px-2 py-0.5 rounded-full font-medium">Online</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[{ label: 'Today\'s Patients', val: '12', color: 'bg-orange-50 text-orange-600' },{ label: 'Pending Rx', val: '5', color: 'bg-blue-50 text-blue-600' },{ label: 'AI Alerts', val: '3', color: 'bg-red-50 text-red-600' }].map(s => (
            <div key={s.label} className={`p-2 rounded-lg text-center ${s.color}`}>
              <div className="text-lg font-bold">{s.val}</div>
              <div className="text-[9px] font-medium">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          {[{ name: 'Amit S.', time: '10:30 AM', tag: 'Follow-up', urgent: false },{ name: 'Priya R.', time: '11:00 AM', tag: 'AI Flagged', urgent: true },{ name: 'Raj K.', time: '11:30 AM', tag: 'New Patient', urgent: false }].map(p => (
            <div key={p.name} className="flex items-center gap-2 p-1.5 bg-gray-50 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center text-[10px] font-bold text-orange-600">{p.name.charAt(0)}</div>
              <div className="flex-1"><div className="text-[10px] font-semibold">{p.name}</div><div className="text-[9px] text-gray-400">{p.time}</div></div>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${p.urgent ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>{p.tag}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    'clinical-insights': (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-violet-500" /><span className="text-xs font-bold text-gray-700">AI Clinical Summary — Patient: Amit S.</span></div>
        <div className="p-2 bg-violet-50 rounded-lg border border-violet-200">
          <div className="text-[10px] text-violet-600 font-semibold mb-1">DIFFERENTIAL DIAGNOSIS</div>
          <div className="space-y-1">
            {[{ dx: 'Type 2 Diabetes Mellitus', conf: '87%' },{ dx: 'Metabolic Syndrome', conf: '72%' },{ dx: 'Hypothyroidism', conf: '34%' }].map(d => (
              <div key={d.dx} className="flex items-center justify-between">
                <span className="text-[10px] text-gray-700">{d.dx}</span>
                <span className="text-[10px] font-bold text-violet-600">{d.conf}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="p-2 bg-orange-50 rounded-lg border border-orange-200">
          <div className="text-[10px] text-orange-600 font-semibold">RECOMMENDED ACTIONS</div>
          <div className="text-[9px] text-gray-600 mt-1">- Order HbA1c, Fasting Insulin, Lipid Panel</div>
          <div className="text-[9px] text-gray-600">- Consider Metformin 500mg initiation</div>
          <div className="text-[9px] text-gray-600">- Refer to Endocrinology if HbA1c &gt; 7%</div>
        </div>
      </div>
    ),
    'prescription': (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><Pill className="w-4 h-4 text-orange-500" /><span className="text-xs font-bold text-gray-700">Digital Prescription</span></div>
        <div className="space-y-1.5">
          {[{ drug: 'Metformin 500mg', dose: '1-0-1, After food', days: '30 days' },{ drug: 'Atorvastatin 10mg', dose: '0-0-1, At bedtime', days: '30 days' }].map(m => (
            <div key={m.drug} className="p-2 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-[10px] font-semibold text-gray-800">{m.drug}</div>
              <div className="text-[9px] text-gray-500">{m.dose} | {m.days}</div>
            </div>
          ))}
        </div>
        <div className="p-2 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-center gap-1"><AlertCircle className="w-3 h-3 text-red-500" /><span className="text-[10px] font-semibold text-red-600">AI Drug Interaction Alert</span></div>
          <div className="text-[9px] text-red-500 mt-0.5">Moderate interaction: Metformin + Alcohol — advise patient</div>
        </div>
      </div>
    ),
    'lab-review': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700 flex items-center gap-1"><Eye className="w-3 h-3 text-blue-500" /> AI Lab Review — Trend Analysis</div>
        <div className="grid grid-cols-3 gap-1.5">
          {[{ test: 'HbA1c', prev: '6.2%', curr: '6.8%', trend: 'up' },{ test: 'LDL', prev: '130', curr: '142', trend: 'up' },{ test: 'TSH', prev: '3.1', curr: '5.8', trend: 'up' }].map(t => (
            <div key={t.test} className="p-2 bg-white rounded-lg border text-center">
              <div className="text-[9px] font-medium text-gray-500">{t.test}</div>
              <div className="text-[10px] font-bold text-gray-800">{t.curr}</div>
              <div className={`text-[9px] ${t.trend === 'up' ? 'text-red-500' : 'text-green-500'}`}>{t.trend === 'up' ? '↑' : '↓'} from {t.prev}</div>
            </div>
          ))}
        </div>
        <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
          <div className="text-[10px] font-semibold text-amber-700">AI Insight</div>
          <div className="text-[9px] text-amber-600">Progressive metabolic deterioration over 6 months. TSH elevation suggests subclinical hypothyroidism — correlates with lipid increase.</div>
        </div>
      </div>
    ),
    'telemedicine': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700">HD Video Consultation</div>
        <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg h-24 flex items-center justify-center">
          <Video className="w-8 h-8 text-white/30" />
          <div className="absolute bottom-2 left-2 flex gap-1">
            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center"><Phone className="w-2.5 h-2.5 text-white" /></div>
            <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center"><Monitor className="w-2.5 h-2.5 text-white" /></div>
          </div>
          <div className="absolute top-2 right-2 text-[9px] text-green-400 font-mono">LIVE 00:14:32</div>
          <div className="absolute bottom-2 right-2 bg-gray-700/80 rounded px-1.5 py-0.5 text-[9px] text-white">Dr. Priya M.</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-1.5 bg-blue-50 rounded-lg text-center"><div className="text-[9px] text-blue-600 font-medium">Screen Share</div></div>
          <div className="p-1.5 bg-violet-50 rounded-lg text-center"><div className="text-[9px] text-violet-600 font-medium">AI Transcription</div></div>
        </div>
      </div>
    ),
    'location-matching': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700">Your Doctor Profile — Public View</div>
        <div className="flex items-center gap-3 p-2 bg-orange-50 rounded-lg border border-orange-200">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-white font-bold text-sm">P</div>
          <div>
            <div className="text-xs font-semibold">Dr. Priya Mehta</div>
            <div className="text-[9px] text-gray-500">Cardiology | 12 yrs exp</div>
            <div className="flex items-center gap-1 mt-0.5"><Star className="w-3 h-3 text-amber-400 fill-current" /><span className="text-[10px] font-bold">4.9</span><span className="text-[9px] text-gray-400">(234 reviews)</span></div>
          </div>
        </div>
        <div className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400" /><span className="text-[10px] text-gray-600">Bandra West, Mumbai, Maharashtra</span></div>
        <div className="grid grid-cols-2 gap-1.5">
          <div className="p-1.5 bg-green-50 rounded-lg text-center"><div className="text-[10px] font-bold text-green-600">142</div><div className="text-[9px] text-green-500">Patients this month</div></div>
          <div className="p-1.5 bg-blue-50 rounded-lg text-center"><div className="text-[10px] font-bold text-blue-600">98%</div><div className="text-[9px] text-blue-500">Acceptance rate</div></div>
        </div>
      </div>
    ),

    // ===== ADMIN SCREENS =====
    'admin-analytics': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700">Platform Analytics — Live</div>
        <div className="grid grid-cols-4 gap-1.5">
          {[{ label: 'Users', val: '5,24,000+', color: 'bg-blue-50 text-blue-600' },{ label: 'Doctors', val: '12,400+', color: 'bg-orange-50 text-orange-600' },{ label: 'Reports', val: '10L+', color: 'bg-green-50 text-green-600' },{ label: 'Revenue', val: '₹4.2Cr', color: 'bg-purple-50 text-purple-600' }].map(s => (
            <div key={s.label} className={`p-1.5 rounded-lg text-center ${s.color}`}><div className="text-[10px] font-bold">{s.val}</div><div className="text-[8px] font-medium">{s.label}</div></div>
          ))}
        </div>
        <div className="h-16 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg flex items-end px-2 pb-1 gap-0.5">
          {[30,45,35,60,55,70,65,80,75,90,85,95].map((h,i) => (
            <div key={i} className="flex-1 bg-gradient-to-t from-blue-400 to-blue-200 rounded-t transition-all duration-1000" style={{ height: isActive ? `${h}%` : '5%' }} />
          ))}
        </div>
        <div className="text-[9px] text-gray-400 text-center">Monthly Active Users — 12 Month Trend</div>
      </div>
    ),
    'user-management': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700">User & Doctor Management</div>
        <div className="flex gap-2 mb-1">
          {['All Users', 'Pending Verification', 'Flagged'].map((t,i) => (
            <span key={t} className={`text-[9px] px-2 py-1 rounded-full font-medium ${i === 0 ? 'bg-purple-500 text-white' : 'bg-gray-100 text-gray-500'}`}>{t}</span>
          ))}
        </div>
        {[{ name: 'Dr. Arvind K.', role: 'Doctor', status: 'Pending', action: 'Verify' },{ name: 'MedPlus Lab', role: 'Lab', status: 'Active', action: 'View' },{ name: 'City Hospital', role: 'Clinic', status: 'Active', action: 'View' }].map(u => (
          <div key={u.name} className="flex items-center gap-2 p-1.5 bg-gray-50 rounded-lg">
            <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center text-[10px] font-bold text-purple-600">{u.name.charAt(0)}</div>
            <div className="flex-1"><div className="text-[10px] font-semibold">{u.name}</div><div className="text-[9px] text-gray-400">{u.role}</div></div>
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${u.status === 'Pending' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600'}`}>{u.status}</span>
          </div>
        ))}
      </div>
    ),
    'ai-monitoring': (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-violet-500" /><span className="text-xs font-bold text-gray-700">AI Operations Dashboard</span></div>
        <div className="grid grid-cols-2 gap-2">
          {[{ label: 'Analyses Today', val: '3,842', color: 'bg-violet-50 text-violet-600' },{ label: 'Avg Latency', val: '1.2s', color: 'bg-green-50 text-green-600' },{ label: 'Accuracy', val: '97.3%', color: 'bg-blue-50 text-blue-600' },{ label: 'Cost/Analysis', val: '₹0.08', color: 'bg-orange-50 text-orange-600' }].map(m => (
            <div key={m.label} className={`p-2 rounded-lg ${m.color}`}><div className="text-xs font-bold">{m.val}</div><div className="text-[9px] font-medium">{m.label}</div></div>
          ))}
        </div>
        <div className="p-2 bg-green-50 rounded-lg border border-green-200">
          <div className="text-[10px] font-semibold text-green-700">All Models Healthy</div>
          <div className="text-[9px] text-green-600">GPT-4o: Active | Prakriti Engine: Active | OCR Pipeline: Active</div>
        </div>
      </div>
    ),
    'security': (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><Lock className="w-4 h-4 text-purple-500" /><span className="text-xs font-bold text-gray-700">Security & Compliance</span></div>
        <div className="grid grid-cols-2 gap-1.5">
          {[{ label: 'HIPAA', status: 'Compliant', color: 'bg-green-50 text-green-600' },{ label: 'MFA', status: 'Enforced', color: 'bg-green-50 text-green-600' },{ label: 'Brute Force', status: 'Protected', color: 'bg-green-50 text-green-600' },{ label: 'Audit Trail', status: 'Active', color: 'bg-green-50 text-green-600' }].map(s => (
            <div key={s.label} className={`p-1.5 rounded-lg ${s.color} flex items-center gap-1`}><CheckCircle className="w-3 h-3" /><div><div className="text-[10px] font-bold">{s.label}</div><div className="text-[9px]">{s.status}</div></div></div>
          ))}
        </div>
        <div className="p-1.5 bg-amber-50 rounded-lg border border-amber-200">
          <div className="text-[10px] font-semibold text-amber-700">Last 24h: 2 login anomalies detected & blocked</div>
        </div>
      </div>
    ),
    'revenue': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700 flex items-center gap-1"><TrendingUp className="w-3 h-3 text-purple-500" /> Revenue Intelligence</div>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 bg-purple-50 rounded-lg"><div className="text-xs font-bold text-purple-700">₹4.2 Cr</div><div className="text-[9px] text-purple-500">ARR (↑32% YoY)</div></div>
          <div className="p-2 bg-green-50 rounded-lg"><div className="text-xs font-bold text-green-700">75.2%</div><div className="text-[9px] text-green-500">Gross Margin</div></div>
        </div>
        <div className="space-y-1">
          {[{ seg: 'B2C Subscriptions', val: '₹2.8Cr', pct: 67 },{ seg: 'B2B Enterprise', val: '₹1.1Cr', pct: 26 },{ seg: 'Lab Partnerships', val: '₹0.3Cr', pct: 7 }].map(r => (
            <div key={r.seg} className="flex items-center gap-2">
              <span className="text-[9px] text-gray-600 w-24 truncate">{r.seg}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-purple-400 rounded-full transition-all duration-1000" style={{ width: isActive ? `${r.pct}%` : '0%' }} /></div>
              <span className="text-[9px] font-bold text-gray-700 w-12 text-right">{r.val}</span>
            </div>
          ))}
        </div>
      </div>
    ),

    // ===== CLINIC / HOSPITAL SCREENS =====
    'clinic-mode': (
      <div className="space-y-3">
        <div className="text-xs font-bold text-gray-700">Choose Your Facility Type</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-emerald-50 rounded-xl border-2 border-emerald-300 text-center">
            <Building2 className="w-8 h-8 text-emerald-500 mx-auto mb-1" />
            <div className="text-xs font-bold text-emerald-700">Hospital</div>
            <div className="text-[9px] text-emerald-600 mt-1">Multi-dept, Beds, OR, ICU</div>
          </div>
          <div className="p-3 bg-gray-50 rounded-xl border-2 border-gray-200 text-center">
            <Stethoscope className="w-8 h-8 text-gray-400 mx-auto mb-1" />
            <div className="text-xs font-bold text-gray-600">Consultation</div>
            <div className="text-[9px] text-gray-500 mt-1">OPD focused, Appointments</div>
          </div>
        </div>
        <div className="text-[9px] text-gray-400 text-center">Dashboard adapts to your facility type</div>
      </div>
    ),
    'departments': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700">Department Management</div>
        {[{ dept: 'Cardiology', docs: 8, patients: 42, color: 'border-l-red-400' },{ dept: 'Orthopedics', docs: 5, patients: 31, color: 'border-l-blue-400' },{ dept: 'Pediatrics', docs: 6, patients: 28, color: 'border-l-green-400' },{ dept: 'Neurology', docs: 4, patients: 19, color: 'border-l-purple-400' }].map(d => (
          <div key={d.dept} className={`p-2 bg-gray-50 rounded-lg border-l-4 ${d.color}`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-semibold">{d.dept}</div>
              <div className="text-[9px] text-gray-400">{d.docs} doctors | {d.patients} patients</div>
            </div>
          </div>
        ))}
      </div>
    ),
    'patient-flow': (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-emerald-500" /><span className="text-xs font-bold text-gray-700">AI Patient Flow Optimization</span></div>
        <div className="grid grid-cols-3 gap-1.5">
          <div className="p-2 bg-emerald-50 rounded-lg text-center"><div className="text-sm font-bold text-emerald-700">18m</div><div className="text-[8px] text-emerald-500">Avg Wait Time</div><div className="text-[8px] text-green-500">↓ 40% improved</div></div>
          <div className="p-2 bg-blue-50 rounded-lg text-center"><div className="text-sm font-bold text-blue-700">94%</div><div className="text-[8px] text-blue-500">Slot Utilization</div></div>
          <div className="p-2 bg-orange-50 rounded-lg text-center"><div className="text-sm font-bold text-orange-700">11am</div><div className="text-[8px] text-orange-500">Peak Hour Today</div></div>
        </div>
        <div className="p-2 bg-emerald-50 rounded-lg border border-emerald-200">
          <div className="text-[10px] font-semibold text-emerald-700">AI Suggestion</div>
          <div className="text-[9px] text-emerald-600">Shift 3 afternoon Cardiology slots to morning — 28% demand increase predicted for tomorrow</div>
        </div>
      </div>
    ),
    'integrated-lab': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700">Integrated Lab & Pharmacy</div>
        <div className="space-y-1.5">
          {[{ item: 'CBC + ESR — Amit S.', status: 'Results Ready', color: 'bg-green-100 text-green-700' },{ item: 'Thyroid Panel — Priya R.', status: 'Processing', color: 'bg-yellow-100 text-yellow-700' },{ item: 'Lipid Profile — Raj K.', status: 'Sample Collected', color: 'bg-blue-100 text-blue-700' }].map(t => (
            <div key={t.item} className="flex items-center justify-between p-1.5 bg-gray-50 rounded-lg">
              <div className="text-[10px] font-medium text-gray-700">{t.item}</div>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${t.color}`}>{t.status}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="flex-1 p-1.5 bg-green-50 rounded-lg text-center"><div className="text-[10px] font-bold text-green-600">Lab</div><div className="text-[9px] text-green-500">47 tests today</div></div>
          <div className="flex-1 p-1.5 bg-blue-50 rounded-lg text-center"><div className="text-[10px] font-bold text-blue-600">Pharmacy</div><div className="text-[9px] text-blue-500">83 Rx dispensed</div></div>
        </div>
      </div>
    ),
    'operational-analytics': (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-500" /><span className="text-xs font-bold text-gray-700">Operational Intelligence</span></div>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 bg-emerald-50 rounded-lg"><div className="text-xs font-bold text-emerald-700">87%</div><div className="text-[9px] text-emerald-500">Bed Occupancy</div></div>
          <div className="p-2 bg-blue-50 rounded-lg"><div className="text-xs font-bold text-blue-700">₹18.4L</div><div className="text-[9px] text-blue-500">Revenue Today</div></div>
        </div>
        <div className="h-12 flex items-end gap-0.5 px-1">
          {[70,85,60,90,75,95,80,88,92,78].map((h,i) => (
            <div key={i} className="flex-1 bg-gradient-to-t from-emerald-400 to-emerald-200 rounded-t transition-all duration-1000" style={{ height: isActive ? `${h}%` : '5%' }} />
          ))}
        </div>
        <div className="p-1.5 bg-amber-50 rounded-lg border border-amber-200">
          <div className="text-[9px] text-amber-700 font-semibold">AI Forecast: +15% admissions expected next week — plan staffing</div>
        </div>
      </div>
    ),

    // ===== LAB SCREENS =====
    'lab-dashboard': (
      <div className="space-y-2">
        <div className="text-xs font-bold text-gray-700">Lab Control Panel</div>
        <div className="grid grid-cols-4 gap-1.5">
          {[{ label: 'Pending', val: '34', color: 'bg-yellow-50 text-yellow-600' },{ label: 'Processing', val: '18', color: 'bg-blue-50 text-blue-600' },{ label: 'Completed', val: '127', color: 'bg-green-50 text-green-600' },{ label: 'Urgent', val: '5', color: 'bg-red-50 text-red-600' }].map(s => (
            <div key={s.label} className={`p-1.5 rounded-lg text-center ${s.color}`}><div className="text-sm font-bold">{s.val}</div><div className="text-[8px] font-medium">{s.label}</div></div>
          ))}
        </div>
        <div className="space-y-1">
          {[{ cat: 'Hematology', count: 42, color: 'bg-red-200' },{ cat: 'Biochemistry', count: 68, color: 'bg-blue-200' },{ cat: 'Microbiology', count: 23, color: 'bg-green-200' },{ cat: 'Pathology', count: 15, color: 'bg-purple-200' }].map(c => (
            <div key={c.cat} className="flex items-center gap-2">
              <span className="text-[9px] text-gray-600 w-20">{c.cat}</span>
              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full ${c.color} rounded-full transition-all duration-1000`} style={{ width: isActive ? `${(c.count/68)*100}%` : '0%' }} /></div>
              <span className="text-[9px] font-bold text-gray-600 w-6 text-right">{c.count}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    'sample-tracking': (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><Microscope className="w-4 h-4 text-rose-500" /><span className="text-xs font-bold text-gray-700">Sample Tracking</span></div>
        {[{ id: 'SMP-4821', patient: 'Amit S.', test: 'CBC', stage: 'Centrifuge', time: '2m ago' },{ id: 'SMP-4822', patient: 'Priya R.', test: 'Thyroid', stage: 'Analysis', time: '8m ago' },{ id: 'SMP-4823', patient: 'Raj K.', test: 'Lipid', stage: 'Collection', time: 'Just now' }].map(s => (
          <div key={s.id} className="p-2 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-bold text-gray-700">{s.id} — {s.patient}</div>
              <span className="text-[9px] text-gray-400">{s.time}</span>
            </div>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-[9px] text-gray-500">{s.test}</span>
              <ChevronRight className="w-2.5 h-2.5 text-gray-300" />
              <span className="text-[9px] font-medium text-rose-600">{s.stage}</span>
            </div>
          </div>
        ))}
      </div>
    ),
    'ai-interpretation': (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><Brain className="w-4 h-4 text-violet-500" /><span className="text-xs font-bold text-gray-700">AI Result Interpretation</span></div>
        <div className="p-2 bg-red-50 rounded-lg border border-red-200">
          <div className="flex items-center gap-1"><AlertCircle className="w-3 h-3 text-red-500" /><span className="text-[10px] font-semibold text-red-600">Critical Value Alert</span></div>
          <div className="text-[9px] text-red-500 mt-0.5">Potassium: 6.2 mEq/L (Critical High) — Immediate physician notification sent</div>
        </div>
        <div className="p-2 bg-violet-50 rounded-lg border border-violet-200">
          <div className="text-[10px] font-semibold text-violet-600">AI Interpretive Notes</div>
          <div className="text-[9px] text-gray-600 mt-0.5">Elevated WBC (14,200) with neutrophilic predominance suggests bacterial infection. CRP correlation: Positive (24mg/L). Recommend blood culture if febrile.</div>
        </div>
        <div className="text-[9px] text-gray-400 text-center">Auto-generated for physician review</div>
      </div>
    ),
    'quality-control': (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" /><span className="text-xs font-bold text-gray-700">QC & Accreditation</span></div>
        <div className="grid grid-cols-2 gap-2">
          <div className="p-2 bg-green-50 rounded-lg border border-green-200 text-center"><div className="text-xs font-bold text-green-700">NABL</div><div className="text-[9px] text-green-600">Accredited</div></div>
          <div className="p-2 bg-green-50 rounded-lg border border-green-200 text-center"><div className="text-xs font-bold text-green-700">CAP</div><div className="text-[9px] text-green-600">Compliant</div></div>
        </div>
        <div className="space-y-1">
          {[{ item: 'Internal QC Run #47', status: 'Passed', time: 'Today 8:00 AM' },{ item: 'Proficiency Test — EQAS', status: 'Passed', time: 'Mar 2026' },{ item: 'Instrument Calibration', status: 'Due', time: 'In 3 days' }].map(q => (
            <div key={q.item} className="flex items-center justify-between p-1.5 bg-gray-50 rounded-lg">
              <div><div className="text-[10px] font-medium">{q.item}</div><div className="text-[9px] text-gray-400">{q.time}</div></div>
              <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${q.status === 'Passed' ? 'bg-green-100 text-green-600' : 'bg-amber-100 text-amber-600'}`}>{q.status}</span>
            </div>
          ))}
        </div>
      </div>
    ),
    'trend-alerts': (
      <div className="space-y-2">
        <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-rose-500" /><span className="text-xs font-bold text-gray-700">AI Trend Reports & Alerts</span></div>
        <div className="p-2 bg-amber-50 rounded-lg border border-amber-200">
          <div className="text-[10px] font-semibold text-amber-700">Anomaly Detected</div>
          <div className="text-[9px] text-amber-600">HbA1c results trending 12% higher than last month's population mean. Investigate reagent lot #R-4821.</div>
        </div>
        <div className="h-12 flex items-end gap-0.5 px-1">
          {[40,42,38,45,43,48,55,62,68,72,70,75].map((h,i) => (
            <div key={i} className={`flex-1 rounded-t transition-all duration-1000 ${i >= 6 ? 'bg-gradient-to-t from-red-400 to-red-200' : 'bg-gradient-to-t from-rose-300 to-rose-100'}`} style={{ height: isActive ? `${h}%` : '5%' }} />
          ))}
        </div>
        <div className="text-[9px] text-gray-400 text-center">HbA1c Result Distribution — Last 12 weeks</div>
      </div>
    ),
  };

  // Generic screen for non-special visuals
  const defaultScreen = (
    <div className="space-y-3">
      <div className="h-6 bg-gray-100 rounded w-3/4 animate-pulse" />
      <div className="h-4 bg-gray-50 rounded w-full animate-pulse" />
      <div className="grid grid-cols-2 gap-2">
        <div className="h-16 bg-gray-50 rounded-lg animate-pulse" />
        <div className="h-16 bg-gray-50 rounded-lg animate-pulse" />
      </div>
      <div className="h-4 bg-gray-50 rounded w-5/6 animate-pulse" />
    </div>
  );

  return (
    <div className="relative w-full">
      <div className="bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
        {/* Title bar */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 bg-gray-50 border-b">
          <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
          <span className="ml-3 text-[10px] text-gray-400 font-mono">healthtrackpro.app</span>
        </div>
        {/* Content */}
        <div className="p-3 sm:p-4 min-h-[140px] max-h-[28vh] lg:max-h-[40vh] overflow-y-auto">
          {screens[visual] || defaultScreen}
        </div>
      </div>
    </div>
  );
};

// AI Badge component
const AIBadge = () => (
  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-200 rounded-full text-[11px] font-semibold text-violet-600">
    <Brain className="w-3 h-3" />
    AI-Powered
  </span>
);

const PlatformDemo = () => {
  const [activeRole, setActiveRole] = useState('user');
  const [activeStep, setActiveStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [fadeIn, setFadeIn] = useState(true);
  const intervalRef = useRef(null);
  const tour = ROLE_TOURS[activeRole];

  const goToStep = useCallback((stepIdx) => {
    setFadeIn(false);
    setTimeout(() => {
      setActiveStep(stepIdx);
      setFadeIn(true);
    }, 200);
  }, []);

  // Auto-play through steps
  useEffect(() => {
    if (isPlaying) {
      intervalRef.current = setInterval(() => {
        setFadeIn(false);
        setTimeout(() => {
          setActiveStep(prev => {
            const next = (prev + 1) % tour.steps.length;
            return next;
          });
          setFadeIn(true);
        }, 200);
      }, 5000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isPlaying, tour.steps.length]);

  // Reset step on role change
  useEffect(() => {
    setActiveStep(0);
    setFadeIn(true);
  }, [activeRole]);

  const currentStep = tour.steps[activeStep];
  const totalAIFeatures = tour.steps.filter(s => s.aiFeature).length;

  return (
    <div className="min-h-screen bg-slate-950" data-testid="platform-demo-page">
      {/* Subtle grid background */}
      <div className="fixed inset-0 z-0 opacity-[0.03]" style={{
        backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
      }} />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-slate-950/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="demo-logo-link">
            <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Heart className="w-4 h-4 text-white" />
            </div>
            <span className="text-base sm:text-lg font-bold text-white whitespace-nowrap">HealthTrack Pro</span>
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-orange-500/20 text-orange-400 font-semibold hidden xs:inline">DEMO</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/" className="p-2 text-gray-400 hover:text-white transition sm:hidden" data-testid="demo-home-btn" aria-label="Home">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </Link>
            <Link to="/register/health" className="px-3 sm:px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-lg text-xs sm:text-sm hover:opacity-90 transition whitespace-nowrap" data-testid="demo-get-started-btn">
              Get Started <ArrowRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline ml-0.5" />
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-2 pb-1 px-4 sm:px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-white leading-tight" data-testid="demo-title">
            See Every Feature in
            <span className="bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent"> Action</span>
          </h1>
          <p className="text-[11px] text-gray-400 max-w-xl mx-auto hidden sm:block">
            Explore HealthTrack Pro by role. Discover how AI powers modern healthcare.
          </p>
        </div>
      </section>

      {/* Role Tabs */}
      <section className="px-3 sm:px-6 pb-1">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-nowrap overflow-x-auto justify-start sm:justify-center gap-1 sm:gap-1.5 pb-1 scrollbar-none" data-testid="role-tabs">
            {ROLES.map(role => {
              const isActive = activeRole === role.id;
              return (
                <button
                  key={role.id}
                  onClick={() => { setActiveRole(role.id); setIsPlaying(true); }}
                  data-testid={`demo-role-tab-${role.id}`}
                  className={`flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-1.5 rounded-lg font-medium text-[11px] sm:text-xs whitespace-nowrap flex-shrink-0 transition-all duration-300 ${
                    isActive
                      ? `bg-gradient-to-r ${role.color} text-white shadow-lg shadow-${role.id === 'user' ? 'blue' : role.id === 'doctor' ? 'orange' : role.id === 'admin' ? 'purple' : role.id === 'clinic' ? 'emerald' : 'rose'}-500/25`
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/5'
                  }`}
                >
                  <role.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="hidden xs:inline sm:inline">{role.label}</span>
                  <span className="xs:hidden">{role.shortLabel || role.label.split('/')[0].split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Main Demo Area — constrained to viewport on laptops so everything fits */}
      <section className="px-4 sm:px-6 pb-2 overflow-x-hidden lg:h-[calc(100dvh-152px)] lg:flex lg:flex-col" data-testid="demo-main-section">
        <div className="max-w-6xl mx-auto w-full lg:flex-1 lg:min-h-0 lg:flex lg:flex-col">
          {/* Role subtitle */}
          <div className="text-center mb-1 lg:mb-2 flex-shrink-0">
            <h2 className="text-xs lg:text-sm font-semibold text-white" data-testid="role-tour-title">{tour.title}</h2>
          </div>
          <div className="grid lg:grid-cols-5 gap-3 lg:gap-4 max-w-full overflow-x-hidden lg:flex-1 lg:min-h-0">
            {/* Step List (left) — natural height on mobile, viewport-fit on lg+ */}
            <div className="lg:col-span-2 space-y-1 lg:max-h-full lg:overflow-y-auto scrollbar-thin lg:pr-1" data-testid="step-list">
              {tour.steps.map((step, idx) => {
                const isActiveStep = idx === activeStep;
                return (
                  <button
                    key={idx}
                    onClick={() => { goToStep(idx); setIsPlaying(false); }}
                    data-testid={`demo-step-${idx}`}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-all duration-300 group ${
                      isActiveStep
                        ? 'bg-white/10 border border-white/10'
                        : 'hover:bg-white/5 border border-transparent'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
                        isActiveStep ? `bg-gradient-to-br ${ROLES.find(r => r.id === activeRole).color}` : 'bg-white/5 group-hover:bg-white/10'
                      }`}>
                        <step.icon className={`w-4 h-4 ${isActiveStep ? 'text-white' : 'text-gray-500 group-hover:text-gray-300'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-medium truncate ${isActiveStep ? 'text-white' : 'text-gray-400 group-hover:text-gray-200'}`}>
                            {step.title}
                          </span>
                          {step.aiFeature && <Brain className="w-3 h-3 text-violet-400 flex-shrink-0" />}
                        </div>
                        {isActiveStep && (
                          <div className="mt-1.5 h-1 bg-white/10 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-orange-400 to-amber-400 rounded-full"
                              style={{
                                animation: isPlaying ? 'progressBar 5s linear infinite' : 'none',
                                width: isPlaying ? '100%' : '50%',
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
              {/* Playback controls */}
              <div className="flex items-center gap-2 px-4 pt-3">
                <button
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white text-xs transition"
                  data-testid="demo-play-pause"
                >
                  {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                  {isPlaying ? 'Pause' : 'Play'}
                </button>
                <button
                  onClick={() => { goToStep(0); setIsPlaying(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white text-xs transition"
                  data-testid="demo-restart"
                >
                  <RotateCcw className="w-3 h-3" />
                  Restart
                </button>
              </div>
            </div>

            {/* Feature Detail + Mock Screen (right) */}
            <div className="lg:col-span-3 lg:min-h-0 lg:overflow-y-auto scrollbar-thin lg:pr-1">
              <div className={`transition-all duration-300 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
                {/* Mock screen */}
                <MockScreen visual={currentStep.visual} accent={ROLES.find(r => r.id === activeRole).accent} isActive={fadeIn} />

                {/* Feature description */}
                <div className="mt-2 lg:mt-3 p-3 lg:p-4 bg-white/5 rounded-xl border border-white/5" data-testid="feature-detail">
                  <div className="flex items-start gap-2">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${ROLES.find(r => r.id === activeRole).color} flex items-center justify-center flex-shrink-0`}>
                      <currentStep.icon className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-white">{currentStep.title}</h3>
                        {currentStep.aiFeature && <AIBadge />}
                      </div>
                      <p className="text-gray-400 text-xs leading-relaxed line-clamp-2">{currentStep.description}</p>
                    </div>
                  </div>

                  {/* AI Deep Dive */}
                  {currentStep.aiFeature && currentStep.aiDetail && (
                    <div className="mt-2 lg:mt-3 p-2 lg:p-3 bg-gradient-to-r from-violet-500/5 to-fuchsia-500/5 border border-violet-500/20 rounded-lg" data-testid="ai-deep-dive">
                      <div className="flex items-center gap-2 mb-1">
                        <Sparkles className="w-3 h-3 text-violet-400" />
                        <span className="text-[10px] font-semibold text-violet-400 uppercase tracking-wide">AI Deep Dive</span>
                      </div>
                      <p className="text-xs text-violet-200/70 leading-relaxed line-clamp-2">{currentStep.aiDetail}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Scroll-down hint (laptop only) */}
          <div className="hidden lg:flex items-center justify-center gap-2 pt-1 flex-shrink-0">
            <span className="text-[10px] text-gray-500">More below</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500 animate-bounce"><polyline points="6 9 12 15 18 9"/></svg>
          </div>
        </div>
      </section>

      {/* ===== FOLD 2: AI Showcase + CTA (fits in one viewport on laptops) ===== */}
      <div className="lg:min-h-[calc(100dvh-56px)] lg:flex lg:flex-col lg:justify-center">
        {/* AI Analysis Showcase Section */}
        <section className="px-4 sm:px-6 py-6 lg:py-4 bg-gradient-to-b from-transparent via-violet-500/5 to-transparent" data-testid="ai-showcase-section">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-violet-500/10 border border-violet-500/20 rounded-full text-violet-400 text-[11px] font-medium mb-1.5">
                <Brain className="w-3 h-3" />
                AI Analysis Engine
              </div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white">
                AI That Understands
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-fuchsia-400"> Healthcare</span>
              </h2>
              <p className="text-gray-400 text-[11px] lg:text-xs max-w-xl mx-auto">
                Dual medical analysis engine processes data through Allopathic and Ayurvedic paradigms.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-3 lg:gap-4">
              {[
                {
                  title: 'Dual Medical Engine',
                  desc: 'Every lab report is analyzed through Western biomarkers AND Ayurvedic Prakriti mapping simultaneously, giving you a 360-degree health view.',
                  icon: Brain,
                  stats: '97.3% accuracy',
                  gradient: 'from-orange-500 to-amber-500',
                },
                {
                  title: 'Predictive Health Scoring',
                  desc: 'LSTM neural networks analyze your lab history to predict future health trajectories and recommend preventive measures before conditions develop.',
                  icon: TrendingUp,
                  stats: '12+ risk categories',
                  gradient: 'from-violet-500 to-fuchsia-500',
                },
                {
                  title: 'Personalized AI Wellness',
                  desc: 'Combines lab results, Prakriti type, wearable data, and lifestyle factors to generate daily meal plans, exercise routines, and sleep optimization.',
                  icon: Sparkles,
                  stats: 'Updated daily',
                  gradient: 'from-emerald-500 to-teal-500',
                },
              ].map((feature, idx) => (
                <div key={idx} className="group p-3 lg:p-4 bg-white/5 rounded-xl border border-white/5 hover:border-white/10 transition-all hover:-translate-y-0.5 duration-300" data-testid={`ai-feature-card-${idx}`}>
                  <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-2 group-hover:scale-110 transition-transform`}>
                    <feature.icon className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-[13px] font-semibold text-white mb-0.5">{feature.title}</h3>
                  <p className="text-[11px] text-gray-400 leading-snug mb-1.5 line-clamp-2">{feature.desc}</p>
                  <div className="text-[10px] font-semibold text-orange-400">{feature.stats}</div>
                </div>
              ))}
            </div>

            {/* AI Flow Diagram — compact */}
            <div className="mt-4 p-3 lg:p-4 bg-white/5 rounded-xl border border-white/5" data-testid="ai-flow-diagram">
              <h3 className="text-[13px] font-semibold text-white text-center mb-3">How AI Analysis Works Across Roles</h3>
              <div className="flex flex-col md:flex-row items-center justify-center gap-3 lg:gap-4">
                {[
                  { label: 'Patient uploads lab report', icon: Upload, color: 'bg-blue-500' },
                  { label: 'AI OCR extracts values', icon: Eye, color: 'bg-violet-500' },
                  { label: 'Dual analysis engine runs', icon: Brain, color: 'bg-fuchsia-500' },
                  { label: 'Doctor reviews AI insights', icon: Stethoscope, color: 'bg-orange-500' },
                  { label: 'Patient gets wellness plan', icon: Sparkles, color: 'bg-emerald-500' },
                ].map((step, idx) => (
                  <React.Fragment key={idx}>
                    <div className="flex flex-col items-center text-center w-24 lg:w-28">
                      <div className={`w-10 h-10 lg:w-11 lg:h-11 rounded-xl ${step.color} flex items-center justify-center mb-1.5`}>
                        <step.icon className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
                      </div>
                      <span className="text-[10px] lg:text-[11px] text-gray-400 leading-tight">{step.label}</span>
                    </div>
                    {idx < 4 && <ChevronRight className="w-4 h-4 text-gray-600 hidden md:block flex-shrink-0" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-4 sm:px-6 py-5 lg:py-3">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1">Ready to Transform Your Healthcare Experience?</h2>
            <p className="text-gray-400 text-xs lg:text-sm mb-3 lg:mb-4">Join thousands of healthcare providers already using HealthTrack Pro.</p>
            <div className="flex flex-col sm:flex-row gap-2 lg:gap-3 justify-center">
              <Link
                to="/register/health"
                className="px-5 lg:px-6 py-2.5 lg:py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold rounded-xl hover:opacity-90 flex items-center justify-center gap-2 transition text-sm"
                data-testid="demo-cta-register"
              >
                Get Started Free <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/login/health"
                className="px-5 lg:px-6 py-2.5 lg:py-3 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/15 border border-white/10 flex items-center justify-center gap-2 transition text-sm"
                data-testid="demo-cta-login"
              >
                Login to Dashboard
              </Link>
            </div>
          </div>
        </section>
      </div>

      {/* CSS for progress animation */}
      <style>{`
        @keyframes progressBar {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
      <MobileQRButton />
    </div>
  );
};

export default PlatformDemo;
